import type { LocationQuery, RouteLocationRaw } from 'vue-router'

// Builds the `/getstarted` redirect for an unauthenticated visitor. Carries
// the original path through `r` (so we can return after auth) and hoists
// `referrer` to the top level so signup pages can read it directly.
export const buildLoginRedirect = (from: {
  fullPath: string
  query: LocationQuery
}): RouteLocationRaw => ({
  path: '/getstarted',
  query: {
    r: from.fullPath,
    referrer: from.query.referrer as string | undefined
  }
})

export const useGetstartedRedirect = () => {
  const route = useRoute()
  const runtimeConfig = useRuntimeConfig()

  const parseAllowlist = (raw: unknown): string[] => {
    if (typeof raw !== 'string' || !raw) {
      return []
    }

    return raw
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0)
  }

  const getValidatedRedirectUri = (): string | null => {
    const redirectUri = route.query.redirect_uri

    if (typeof redirectUri !== 'string' || !redirectUri) {
      return null
    }

    const allowlist = parseAllowlist(runtimeConfig.public.allowedAuthRedirectUris)

    if (!allowlist.includes(redirectUri)) {
      return null
    }

    return redirectUri
  }

  const buildExternalRedirectUrl = (redirectUri: string, userId: number | string): string => {
    const url = new URL(redirectUri)

    url.searchParams.set('app_user_id', String(userId))

    const stateParam = route.query.state

    if (typeof stateParam === 'string' && stateParam) {
      url.searchParams.set('state', stateParam)
    }

    return url.toString()
  }

  const propagateAuthQuery = (url: string): RouteLocationRaw => {
    const [path, queryString] = url.split('?')

    const existingQuery = queryString
      ? Object.fromEntries(new URLSearchParams(queryString))
      : {}

    return {
      path,
      query: {
        ...existingQuery,
        r: route.query.r as string | undefined,
        redirect_uri: route.query.redirect_uri as string | undefined,
        state: route.query.state as string | undefined,
        referrer: route.query.referrer as string | undefined
      }
    }
  }

  const redirectAfterAuth = (userId: number | string) => {
    const redirectUri = getValidatedRedirectUri()

    if (redirectUri) {
      return navigateTo(buildExternalRedirectUrl(redirectUri, userId), { external: true })
    }

    return navigateTo((route.query.r as string) || '/feed')
  }

  const hasValidRedirectUri = () => getValidatedRedirectUri() !== null

  return {
    propagateAuthQuery,
    redirectAfterAuth,
    hasValidRedirectUri
  }
}
